import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import { PencilSimple, Key, Trash, Power } from "@phosphor-icons/react";

export default function Users() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [role, setRole] = useState("accountant");
    const [resetTarget, setResetTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        try {
            await api.post("/users", { email, name, password, role });
            toast.success("User created");
            setOpen(false); setEmail(""); setName(""); setPassword("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const setActive = async (u, active) => {
        try { await api.patch(`/users/${u.id}`, { active }); load(); toast.success(active ? "Activated" : "Disabled"); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const setRoleFor = async (u, r) => {
        try { await api.patch(`/users/${u.id}`, { role: r }); load(); toast.success(`Role updated to ${r}`); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader
                title="Users & roles"
                sub="Add teammates and assign responsibilities. Roles are reassignable."
                right={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="add-user-btn">+ Add user</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-serif text-2xl">New user</DialogTitle></DialogHeader>
                            <div className="space-y-4">
                                <div><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="new-user-name" /></div>
                                <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="new-user-email" type="email" /></div>
                                <div>
                                    <Label>Temporary password</Label>
                                    <Input value={password} onChange={(e) => setPassword(e.target.value)} type="text" data-testid="new-user-password" />
                                    <PasswordHints value={password} email={email} name={name} />
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <Select value={role} onValueChange={setRole}>
                                        <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="accountant">Accountant</SelectItem>
                                            <SelectItem value="hr">HR / People Ops</SelectItem>
                                            <SelectItem value="purchase">Purchase / Sourcing</SelectItem>
                                            <SelectItem value="dispatch">Dispatch (uploads sales invoices)</SelectItem>
                                            <SelectItem value="production">Production</SelectItem>
                                            <SelectItem value="sales_staff">Sales</SelectItem>
                                            <SelectItem value="customer_experience">Customer Experience / QC</SelectItem>
                                            <SelectItem value="employee">Employee (self-serve — own payslips + leave)</SelectItem>
                                            <SelectItem value="founder">Founder</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <DialogFooter><Button onClick={create} data-testid="new-user-submit" style={{ background: "var(--se-brand)", color: "#fff" }}>Create user</Button></DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="p-8">
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="users-table">
                        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th></th></tr></thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`user-row-${u.email}`}>
                                    <td>{u.name}</td>
                                    <td className="text-stone-500">{u.email}</td>
                                    <td>
                                        <Select value={u.role} onValueChange={(r) => setRoleFor(u, r)}>
                                            <SelectTrigger className="w-44 h-8"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="accountant">Accountant</SelectItem>
                                                <SelectItem value="hr">HR</SelectItem>
                                                <SelectItem value="purchase">Purchase</SelectItem>
                                                <SelectItem value="dispatch">Dispatch</SelectItem>
                                                <SelectItem value="production">Production</SelectItem>
                                                <SelectItem value="sales_staff">Sales</SelectItem>
                                                <SelectItem value="customer_experience">Customer Exp / QC</SelectItem>
                                                <SelectItem value="employee">Employee</SelectItem>
                                                <SelectItem value="founder">Founder</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td>
                                        <span className={`se-chip ${u.active === false ? "se-chip-err" : "se-chip-posted"}`}>{u.active === false ? "off" : "on"}</span>
                                    </td>
                                    <td className="text-right">
                                        <div className="inline-flex items-center gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => setEditTarget(u)}
                                                    title="Edit name"
                                                    data-testid={`edit-user-${u.email}`}>
                                                <PencilSimple size={14} />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => setResetTarget(u)}
                                                    title="Reset password"
                                                    data-testid={`reset-pw-${u.email}`}>
                                                <Key size={14} />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => setActive(u, !(u.active !== false))}
                                                    title={u.active === false ? "Enable user" : "Disable user"}
                                                    data-testid={`toggle-${u.email}`}>
                                                <Power size={14} className={u.active === false ? "text-stone-400" : "text-emerald-600"} />
                                            </Button>
                                            <Button variant="ghost" size="sm"
                                                    onClick={() => setDeleteTarget(u)}
                                                    disabled={u.id === me?.id}
                                                    title={u.id === me?.id ? "You cannot delete yourself" : "Delete user"}
                                                    className="hover:text-rose-600"
                                                    data-testid={`delete-user-${u.email}`}>
                                                <Trash size={14} />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <ResetPasswordDialog target={resetTarget} onClose={() => setResetTarget(null)} onSaved={load} />
            <EditUserDialog target={editTarget} onClose={() => setEditTarget(null)} onSaved={load} />
            <DeleteUserDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} onSaved={load} />
        </>
    );
}

function EditUserDialog({ target, onClose, onSaved }) {
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (target) setName(target.name || ""); }, [target]);
    if (!target) return null;

    const submit = async () => {
        const trimmed = name.trim();
        if (!trimmed) return toast.error("Name cannot be empty");
        if (trimmed === target.name) return onClose();
        setSaving(true);
        try {
            await api.patch(`/users/${target.id}`, { name: trimmed });
            toast.success("Name updated");
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="edit-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Edit user</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-xs text-stone-500">
                        Editing <b className="text-stone-800">{target.email}</b>. Email and role are managed elsewhere on this page.
                    </div>
                    <div>
                        <Label>Full name</Label>
                        <Input value={name} onChange={(e) => setName(e.target.value)}
                               onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                               data-testid="edit-user-name" autoFocus />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !name.trim()}
                            style={{ background: "var(--se-brand)", color: "#fff" }}
                            data-testid="edit-user-submit">
                        {saving ? "…" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DeleteUserDialog({ target, onClose, onSaved }) {
    const [confirmText, setConfirmText] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (target) setConfirmText(""); }, [target]);
    if (!target) return null;

    const canDelete = confirmText.trim().toLowerCase() === (target.email || "").toLowerCase();

    const submit = async () => {
        if (!canDelete) return;
        setBusy(true);
        try {
            await api.delete(`/users/${target.id}`);
            toast.success(`Deleted ${target.email}`);
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="delete-user-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-rose-700">Delete user</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-stone-700">
                        This will permanently delete <b>{target.name}</b> ({target.email}).
                        Their audit-log history stays intact, but they can no longer sign in.
                        <span className="block mt-2 text-stone-500 text-xs">
                            Prefer &quot;Disable&quot; (the power icon) if you just want to revoke access temporarily.
                        </span>
                    </div>
                    <div>
                        <Label>Type the user&apos;s email to confirm</Label>
                        <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                               placeholder={target.email}
                               data-testid="delete-user-confirm-input" autoFocus />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={!canDelete || busy}
                            style={{ background: canDelete ? "#dc2626" : "#f3f4f6", color: canDelete ? "#fff" : "#9ca3af" }}
                            data-testid="delete-user-submit">
                        {busy ? "Deleting…" : "Delete user"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ResetPasswordDialog({ target, onClose, onSaved }) {
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (target) { setPw(""); setPw2(""); } }, [target]);
    if (!target) return null;

    const submit = async () => {
        if (pw.length < 8) return toast.error("Password must be at least 8 characters");
        if (pw !== pw2) return toast.error("Passwords do not match");
        setSaving(true);
        try {
            await api.patch(`/users/${target.id}`, { password: pw });
            toast.success(`Password reset for ${target.email}. Share it securely.`);
            onClose(); onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent data-testid="reset-pw-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Reset password</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-stone-600">
                        Setting a new password for <b>{target.name}</b> ({target.email}). The user will need to log in again with this new password.
                    </div>
                    <div>
                        <Label>New password</Label>
                        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-new" placeholder="10+ chars, mixed case, digit, symbol" />
                        <PasswordHints value={pw} email={target?.email} name={target?.name} />
                    </div>
                    <div>
                        <Label>Confirm new password</Label>
                        <Input type="text" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-pw-confirm" />
                    </div>
                    <div className="text-[11px] text-amber-700">
                        Ensure you share the new password with the user through a secure channel. This action is logged in the audit trail. Setting a new password will also sign the user out of every device.
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !pw || !pw2} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="reset-pw-submit">
                        {saving ? "…" : "Reset password"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function PasswordHints({ value, email, name }) {
    const v = value || "";
    const local = (email || "").split("@")[0].toLowerCase();
    const first = (name || "").trim().split(" ")[0].toLowerCase();
    const checks = [
        { ok: v.length >= 10, txt: "10+ characters" },
        { ok: /[A-Z]/.test(v) && /[a-z]/.test(v), txt: "Upper + lower case" },
        { ok: /\d/.test(v), txt: "At least one digit" },
        { ok: /[^A-Za-z0-9]/.test(v), txt: "At least one symbol" },
        { ok: !local || !v.toLowerCase().includes(local), txt: "Doesn't contain email" },
        { ok: !first || first.length < 4 || !v.toLowerCase().includes(first), txt: "Doesn't contain name" },
    ];
    if (!v) return null;
    return (
        <ul className="mt-1.5 text-[10px] space-y-0.5">
            {checks.map((c, i) => (
                <li key={i} className={c.ok ? "text-emerald-600" : "text-stone-400"}>
                    {c.ok ? "✓" : "○"} {c.txt}
                </li>
            ))}
        </ul>
    );
}
